import React, { useState, useEffect } from 'react';
import {
    Upload,
    FolderPlus,
    File,
    Folder,
    Share2,
    Lock,
    Unlock,
    Download,
    MoreVertical,
    HardDrive,
    AlertCircle,
    ArrowLeft,
    Edit3,
    Trash2,
    X,
    ZoomIn
} from 'lucide-react';
import { supabase } from '../services/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import Button from '../components/Button';
import { ToastContainer } from '../components/Toast';
import * as driverFunctions from '../utils/driverFunctions';

// Linha abaixo adiciona o ToastContainer ao final do JSX
// Para evitar erro de não utilizado

interface DriveItemUI {
    id: string;
    name: string;
    type: 'file' | 'folder';
    size?: number;
    created_at: string;
    updated_at: string;
    parent_id?: string;
    is_shared: boolean;
    share_password?: string;
    share_link?: string;
    url_arquivo?: string;
    caminho?: string;
    pai_id?: string;
    exige_senha?: boolean;
}

interface ShareModalData {
    item: DriveItemUI | null;
    isOpen: boolean;
    password: string;
    isPasswordProtected: boolean;
}

interface ProtectionModalData {
    item: DriveItemUI | null;
    isOpen: boolean;
    password: string;
}

interface ConfirmModalData {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
}

export default function MeuDriver() {
    const { user } = useAuth();
    const { showSuccess, showError, toasts } = useToast();
    const [items, setItems] = useState<DriveItemUI[]>([]);
    const [currentPath, setCurrentPath] = useState<string[]>([]);
    const [currentPastaId, setCurrentPastaId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [folderModalOpen, setFolderModalOpen] = useState(false);
    const [shareModal, setShareModal] = useState<ShareModalData>({
        item: null,
        isOpen: false,
        password: '',
        isPasswordProtected: false
    });
    const [protectionModal, setProtectionModal] = useState<ProtectionModalData>({
        item: null,
        isOpen: false,
        password: ''
    });
    const [confirmModal, setConfirmModal] = useState<ConfirmModalData>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { }
    });
    const [newFolderName, setNewFolderName] = useState('');
    const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
    const [spaceUsed, setSpaceUsed] = useState({ used: 0, limit: 5 * 1024 * 1024 * 1024 }); // 5GB
    const [renameModal, setRenameModal] = useState<{ isOpen: boolean; item: DriveItemUI | null; newName: string }>({
        isOpen: false,
        item: null,
        newName: ''
    });
    const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null); // ID do item com menu aberto
    const [uploadProgress, setUploadProgress] = useState<{
        isVisible: boolean;
        current: number;
        total: number;
        files: { name: string; status: 'uploading' | 'completed' | 'error' }[];
    }>({
        isVisible: false,
        current: 0,
        total: 0,
        files: []
    });
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set()); // IDs dos itens selecionados
    const [isMultiSelectMode, setIsMultiSelectMode] = useState(false); // Modo de seleção múltipla
    const [lightbox, setLightbox] = useState<{ isOpen: boolean; url: string | null; name: string }>({
        isOpen: false,
        url: null,
        name: ''
    });

    // Carregar espaço usado
    const loadSpaceUsedData = async () => {
        try {
            const used = await driverFunctions.calculateSpaceUsed();
            setSpaceUsed(prev => ({ ...prev, used }));
        } catch (err) {
            console.error('Erro ao calcular espaço usado:', err);
        }
    };

    // Navegar para dentro de uma pasta
    const entrarNaPasta = (pastaId: string, pastaName: string) => {
        setCurrentPastaId(pastaId);
        setCurrentPath([...currentPath, pastaName]);
    };

    // Voltar para pasta anterior
    const voltarPasta = () => {
        if (currentPath.length > 0) {
            const novoCaminho = [...currentPath];
            novoCaminho.pop();
            setCurrentPath(novoCaminho);

            // Se voltou à raiz
            if (novoCaminho.length === 0) {
                setCurrentPastaId(null);
            }
        }
    };

    // Calcular porcentagem de espaço usado
    const spacePercentage = ((spaceUsed.used / spaceUsed.limit) * 100).toFixed(1);
    const spaceUsedMB = (spaceUsed.used / (1024 * 1024)).toFixed(0);
    const spaceLimitGB = (spaceUsed.limit / (1024 * 1024 * 1024)).toFixed(1);

    useEffect(() => {
        if (user) {
            loadItems();
            loadSpaceUsedData();
        }
    }, [user, currentPastaId]);

    const loadItems = async () => {
        try {
            setLoading(true);
            setError('');

            if (!user) {
                setError('Usuário não autenticado');
                return;
            }

            // Buscar itens do banco de dados
            let query = supabase
                .from('driver_items')
                .select('*')
                .eq('user_id', user.id); // FILTRO IMPORTANTE: apenas itens do usuário

            // Se está na raiz (currentPastaId é null), buscar itens sem pai_id
            if (currentPastaId === null || currentPastaId === undefined) {
                query = query.is('pai_id', null);
            } else {
                query = query.eq('pai_id', currentPastaId);
            }

            const { data, error } = await query
                .order('tipo', { ascending: true })
                .order('nome', { ascending: true });

            if (error) throw error;

            // Converter para formato da UI
            const uiItems: DriveItemUI[] = (data || []).map(item => {
                // Converter tipo do banco para UI
                const type = item.tipo === 'pasta' ? 'folder' : 'file';

                return {
                    id: item.id,
                    name: item.nome,
                    type,
                    size: item.tamanho,
                    created_at: item.criado_em,
                    updated_at: item.atualizado_em,
                    parent_id: item.pai_id,
                    is_shared: item.compartilhado,
                    share_password: item.senha_compartilhamento,
                    share_link: item.compartilhamento_token
                        ? `${window.location.origin}/drive/shared/${item.compartilhamento_token}`
                        : undefined,
                    url_arquivo: item.url_arquivo,
                    caminho: item.caminho,
                    pai_id: item.pai_id,
                    exige_senha: item.exige_senha
                };
            });

            // Buscar URLs públicas para arquivos (caso não estejam armazenadas)
            const itemsWithUrls = await Promise.all(uiItems.map(async (item) => {
                if (item.type === 'file' && item.caminho) {
                    try {
                        if (item.url_arquivo) {
                            return item;
                        }

                        const storageBucket = supabase.storage.from('user-driver');

                        // Tentar obter URL pública; caso o bucket seja privado, gerar URL assinada
                        const { data: publicData } = storageBucket.getPublicUrl(item.caminho);

                        if (publicData?.publicUrl) {
                            return {
                                ...item,
                                url_arquivo: publicData.publicUrl ?? undefined
                            };
                        }

                        const { data: signedData, error: signedError } = await storageBucket.createSignedUrl(item.caminho, 60 * 60);

                        if (signedError) {
                            console.warn('Erro ao gerar URL assinada para', item.name, signedError);
                            return item;
                        }

                        return {
                            ...item,
                            url_arquivo: signedData?.signedUrl ?? undefined
                        };
                    } catch (err) {
                        console.warn('Erro inesperado ao obter URL pública:', err);
                        return item;
                    }
                }
                return item;
            }));

            setItems(itemsWithUrls);
        } catch (err) {
            console.error('Erro ao carregar itens:', err);
            setError('Erro ao carregar arquivos e pastas');
            showError('Erro ao carregar arquivos', 'Não foi possível carregar seus arquivos');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async () => {
        if (!uploadFiles || uploadFiles.length === 0 || !user) return;

        try {
            setError('');
            setUploadModalOpen(false);

            // Inicializar progresso
            const fileArray = Array.from(uploadFiles);
            setUploadProgress({
                isVisible: true,
                current: 0,
                total: fileArray.length,
                files: fileArray.map(file => ({ name: file.name, status: 'uploading' as const }))
            });

            // Upload de cada arquivo
            for (let i = 0; i < fileArray.length; i++) {
                const file = fileArray[i];

                try {
                    await driverFunctions.uploadFile(file, currentPastaId, user.id);

                    // Marcar como concluído
                    setUploadProgress(prev => ({
                        ...prev,
                        current: i + 1,
                        files: prev.files.map((f, idx) =>
                            idx === i ? { ...f, status: 'completed' as const } : f
                        )
                    }));

                    showSuccess('Upload concluído', `${file.name} foi enviado com sucesso`);
                } catch (err) {
                    console.error('Erro no upload de', file.name, err);

                    // Marcar como erro
                    setUploadProgress(prev => ({
                        ...prev,
                        files: prev.files.map((f, idx) =>
                            idx === i ? { ...f, status: 'error' as const } : f
                        )
                    }));
                }
            }

            // Aguardar 2 segundos antes de fechar
            setTimeout(() => {
                setUploadProgress(prev => ({ ...prev, isVisible: false }));
                setUploadFiles(null);
                loadItems();
                loadSpaceUsedData();
            }, 2000);

        } catch (err) {
            console.error('Erro no upload:', err);
            setError('Erro ao fazer upload dos arquivos');
            showError('Erro no upload', 'Não foi possível fazer upload dos arquivos');
            setUploadProgress(prev => ({ ...prev, isVisible: false }));
        }
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim() || !user) {
            console.log('❌ handleCreateFolder: validação falhou', { newFolderName, hasUser: !!user });
            return;
        }

        try {
            console.log('📁 Criando pasta...', { nome: newFolderName, currentPastaId, userId: user.id });
            setError('');

            const folderId = await driverFunctions.createFolder(newFolderName.trim(), currentPastaId, user.id);
            console.log('✅ Pasta criada com sucesso:', folderId);

            showSuccess('Pasta criada', `A pasta "${newFolderName}" foi criada com sucesso`);
            setFolderModalOpen(false);
            setNewFolderName('');
            loadItems();
            loadSpaceUsedData();
        } catch (err: any) {
            console.error('❌ Erro ao criar pasta:', err);
            console.error('❌ Detalhes do erro:', JSON.stringify(err, null, 2));
            setError(`Erro ao criar pasta: ${err?.message || 'Erro desconhecido'}`);
            showError('Erro', `Não foi possível criar a pasta: ${err?.message || 'Tente novamente'}`);
        }
    };

    const handleShare = async (item: DriveItemUI) => {
        // Se o item já está compartilhado, copiar o link
        if (item.is_shared && item.share_link) {
            try {
                await navigator.clipboard.writeText(item.share_link);
                showSuccess('Link copiado!', 'O link de compartilhamento foi copiado para a área de transferência');
            } catch (err) {
                console.error('Erro ao copiar link:', err);
                showError('Erro', 'Não foi possível copiar o link');
            }
            return;
        }

        // Se não está compartilhado, abrir modal para criar compartilhamento
        setShareModal({
            item,
            isOpen: true,
            password: '',
            isPasswordProtected: false
        });
    };

    const handleShareSubmit = async () => {
        if (!shareModal.item) return;

        try {
            setError('');
            const { url } = await driverFunctions.shareItem(
                shareModal.item.id,
                shareModal.isPasswordProtected ? shareModal.password : null
            );

            showSuccess('Compartilhamento criado', `Link: ${url}`);

            setShareModal({
                item: null,
                isOpen: false,
                password: '',
                isPasswordProtected: false
            });
            loadItems();
        } catch (err) {
            console.error('Erro ao compartilhar:', err);
            setError('Erro ao compartilhar item');
            showError('Erro', 'Não foi possível compartilhar o item');
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    const imageExtensions = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'avif', 'heic', 'heif']);

    const isImageFile = (item: DriveItemUI) => {
        if (item.type !== 'file') return false;
        const extension = item.name?.split('.').pop()?.toLowerCase() ?? '';
        if (imageExtensions.has(extension)) return true;
        if (item.url_arquivo) {
            const lowerUrl = item.url_arquivo.toLowerCase();
            return Array.from(imageExtensions).some(ext => lowerUrl.includes(`.${ext}`));
        }
        return false;
    };

    const openImageLightbox = (item: DriveItemUI) => {
        if (!item.url_arquivo) return;
        setLightbox({
            isOpen: true,
            url: item.url_arquivo,
            name: item.name
        });
    };

    const closeLightbox = () => {
        setLightbox({
            isOpen: false,
            url: null,
            name: ''
        });
    };

    const handleRename = (item: DriveItemUI) => {
        setRenameModal({ isOpen: true, item, newName: item.name });
        setShowActionsMenu(null);
    };

    const handleRenameSubmit = async () => {
        if (!renameModal.item || !renameModal.newName.trim()) {
            return;
        }

        try {
            setError('');
            await driverFunctions.renameItem(renameModal.item.id, renameModal.newName);
            showSuccess('Item renomeado', `${renameModal.item.name} foi renomeado para "${renameModal.newName}"`);
            setRenameModal({ isOpen: false, item: null, newName: '' });
            loadItems();
        } catch (err: any) {
            console.error('Erro ao renomear:', err);
            setError(`Erro ao renomear: ${err?.message || 'Erro desconhecido'}`);
            showError('Erro', `Não foi possível renomear: ${err?.message || 'Tente novamente'}`);
        }
    };

    const handleDelete = async (item: DriveItemUI) => {
        // Mostrar modal de confirmação
        setConfirmModal({
            isOpen: true,
            title: 'Deletar Item',
            message: `Tem certeza que deseja deletar "${item.name}"? Esta ação não pode ser desfeita.`,
            onConfirm: async () => {
                try {
                    setError('');
                    await driverFunctions.deleteItem(item.id, item.caminho || '', item.type === 'folder' ? 'pasta' : 'arquivo');
                    showSuccess('Item deletado', `${item.name} foi deletado com sucesso`);
                    setShowActionsMenu(null);
                    setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => { } });
                    loadItems();
                    loadSpaceUsedData();
                } catch (err: any) {
                    console.error('Erro ao deletar:', err);
                    setError(`Erro ao deletar: ${err?.message || 'Erro desconhecido'}`);
                    showError('Erro', `Não foi possível deletar: ${err?.message || 'Tente novamente'}`);
                }
            }
        });
    };

    // Toggle seleção de item
    const toggleItemSelection = (itemId: string) => {
        setSelectedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) {
                newSet.delete(itemId);
            } else {
                newSet.add(itemId);
            }
            return newSet;
        });
    };

    // Selecionar todos
    const toggleSelectAll = () => {
        if (selectedItems.size === items.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(items.map(item => item.id)));
        }
    };

    // Deletar vários itens
    const handleBulkDelete = async () => {
        if (selectedItems.size === 0) return;

        const itemNames = items
            .filter(item => selectedItems.has(item.id))
            .map(item => item.name)
            .join(', ');

        // Mostrar modal de confirmação
        setConfirmModal({
            isOpen: true,
            title: 'Deletar Itens',
            message: `Tem certeza que deseja deletar ${selectedItems.size} item(ns)?\n\nItens: ${itemNames}\n\nEsta ação não pode ser desfeita.`,
            onConfirm: async () => {
                try {
                    setError('');
                    setShowActionsMenu(null);
                    setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => { } });

                    // Deletar cada item selecionado
                    for (const itemId of selectedItems) {
                        const item = items.find(i => i.id === itemId);
                        if (item) {
                            try {
                                await driverFunctions.deleteItem(item.id, item.caminho || '', item.type === 'folder' ? 'pasta' : 'arquivo');
                            } catch (err) {
                                console.error('Erro ao deletar', item.name, err);
                            }
                        }
                    }

                    showSuccess(`${selectedItems.size} item(ns) deletado(s)`, 'Os itens foram deletados com sucesso');
                    setSelectedItems(new Set());
                    setIsMultiSelectMode(false);
                    loadItems();
                    loadSpaceUsedData();
                } catch (err: any) {
                    console.error('Erro ao deletar vários:', err);
                    setError(`Erro ao deletar: ${err?.message || 'Erro desconhecido'}`);
                    showError('Erro', `Não foi possível deletar: ${err?.message || 'Tente novamente'}`);
                }
            }
        });
    };

    // Alternar proteção
    const handleToggleProtection = async (item: DriveItemUI) => {
        // Se está protegido, apenas desproteger
        if (item.exige_senha) {
            // Mostrar modal de confirmação
            setConfirmModal({
                isOpen: true,
                title: 'Remover Proteção',
                message: `Tem certeza que deseja remover a proteção de "${item.name}"?`,
                onConfirm: async () => {
                    try {
                        await driverFunctions.toggleProtection(item.id, true);
                        showSuccess('Proteção removida', `A proteção de "${item.name}" foi removida`);
                        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => { } });
                        loadItems();
                    } catch (err: any) {
                        console.error('Erro ao remover proteção:', err);
                        showError('Erro', `Não foi possível remover a proteção: ${err?.message || 'Tente novamente'}`);
                    }
                }
            });
        } else {
            // Se não está protegido, abrir modal para definir senha
            setProtectionModal({
                item,
                isOpen: true,
                password: ''
            });
        }
    };

    // Salvar proteção com senha
    const handleProtectionSubmit = async () => {
        if (!protectionModal.item || !protectionModal.password.trim()) {
            showError('Erro', 'Digite uma senha válida');
            return;
        }

        try {
            setError('');
            await driverFunctions.toggleProtection(
                protectionModal.item.id,
                false,
                protectionModal.password
            );
            showSuccess('Proteção ativada', `"${protectionModal.item.name}" agora está protegido`);
            setProtectionModal({ item: null, isOpen: false, password: '' });
            loadItems();
        } catch (err: any) {
            console.error('Erro ao proteger:', err);
            setError(`Erro ao proteger: ${err?.message || 'Erro desconhecido'}`);
            showError('Erro', `Não foi possível proteger: ${err?.message || 'Tente novamente'}`);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 dark:bg-gray-900 min-h-screen">
            {/* Header */}
            <div className="flex justify-between items-center mb-6" style={{ minHeight: '70px', paddingBottom: '10px' }}>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Meu Driver</h1>
                    <p className="text-gray-600 dark:text-gray-400">Gerencie seus arquivos e pastas</p>
                </div>
                <div className="flex gap-3">
                    {isMultiSelectMode ? (
                        <>
                            <Button
                                variant="btn_add"
                                onClick={() => {
                                    setIsMultiSelectMode(false);
                                    setSelectedItems(new Set());
                                }}
                                className="flex items-center gap-2"
                            >
                                <X size={20} />
                                Cancelar
                            </Button>
                            <Button
                                variant="btn_add"
                                onClick={toggleSelectAll}
                                className="flex items-center gap-2"
                            >
                                {selectedItems.size === items.length ? 'Desselecionar Todos' : 'Selecionar Todos'}
                            </Button>
                            <Button
                                variant="btn_add"
                                onClick={handleBulkDelete}
                                disabled={selectedItems.size === 0}
                                className="flex items-center gap-2"
                            >
                                <Trash2 size={20} />
                                Deletar ({selectedItems.size})
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="btn_add"
                                onClick={() => setIsMultiSelectMode(true)}
                                className="flex items-center gap-2"
                            >
                                <Edit3 size={20} />
                                Selecionar
                            </Button>
                            <Button
                                variant="btn_add"
                                onClick={() => setUploadModalOpen(true)}
                                className="flex items-center gap-2"
                            >
                                <Upload size={20} />
                                Upload Arquivos
                            </Button>
                            <Button
                                variant="btn_add"
                                onClick={() => setFolderModalOpen(true)}
                                className="flex items-center gap-2"
                            >
                                <FolderPlus size={20} />
                                Nova Pasta
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                    <span className="text-red-800 dark:text-red-400">{error}</span>
                </div>
            )}

            {/* Breadcrumb */}
            <div className="flex items-center gap-2 mb-6 text-sm text-gray-600 dark:text-gray-400">
                <button
                    onClick={voltarPasta}
                    disabled={currentPath.length === 0}
                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <ArrowLeft size={16} />
                </button>
                <HardDrive size={16} />
                <button onClick={() => { setCurrentPastaId(null); setCurrentPath([]); }} className="hover:underline">
                    Meu Driver
                </button>
                {currentPath.map((path, index) => (
                    <React.Fragment key={index}>
                        <span>/</span>
                        <span>{path}</span>
                    </React.Fragment>
                ))}
            </div>

            {/* Espaço Utilizado */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <HardDrive size={20} className="text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Espaço Utilizado</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{spaceUsedMB} MB de {spaceLimitGB} GB usados</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{spacePercentage}%</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">do limite</div>
                    </div>
                </div>

                {/* Barra de Progresso */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div
                        className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${spacePercentage}%` }}
                    ></div>
                </div>

                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
                    <span>{spaceUsedMB} MB usado</span>
                    <span>{((spaceUsed.limit - spaceUsed.used) / (1024 * 1024 * 1024)).toFixed(1)} GB disponível</span>
                </div>
            </div>

            {/* Files and Folders Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {items.map((item) => (
                    <div
                        key={item.id}
                        onClick={() => {
                            if (item.type === 'folder') {
                                entrarNaPasta(item.id, item.name);
                            }
                        }}
                        className={`bg-white dark:bg-gray-800 rounded-lg border p-4 transition-shadow cursor-pointer ${isMultiSelectMode && selectedItems.has(item.id)
                            ? 'border-blue-500 dark:border-blue-400 shadow-md'
                            : 'border-gray-200 dark:border-gray-700 hover:shadow-md'
                            } ${isMultiSelectMode ? 'hover:border-blue-500 dark:hover:border-blue-400' : ''}`}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                                {isMultiSelectMode && (
                                    <input
                                        type="checkbox"
                                        checked={selectedItems.has(item.id)}
                                        onChange={() => toggleItemSelection(item.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                                    />
                                )}
                                {item.type === 'folder' ? (
                                    <Folder size={24} className="text-blue-500 dark:text-blue-400" />
                                ) : isImageFile(item) && item.url_arquivo ? (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openImageLightbox(item);
                                        }}
                                        className="relative w-12 h-12 flex-shrink-0 rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:ring-2 hover:ring-blue-400 transition-all"
                                        title="Visualizar imagem"
                                    >
                                        <img
                                            src={item.url_arquivo}
                                            alt={`Prévia de ${item.name}`}
                                            className="w-full h-full object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                                            <ZoomIn size={16} />
                                        </div>
                                    </button>
                                ) : (
                                    <File size={24} className="text-gray-500 dark:text-gray-400" />
                                )}
                                <div className="flex-1 min-w-0">
                                    <h3
                                        className="font-medium text-xs text-gray-900 dark:text-gray-100 leading-snug overflow-hidden"
                                        style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            wordBreak: 'break-word'
                                        }}
                                        title={item.name}
                                    >
                                        {item.name}
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {item.type === 'file' && item.size ? formatFileSize(item.size) : 'Pasta'}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 relative">
                                {/* Ícone de Proteção (sempre visível) */}
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleProtection(item);
                                    }}
                                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded transition-colors"
                                    title={item.exige_senha ? "Desproteger (clique aqui)" : "Proteger (clique aqui)"}
                                >
                                    {item.exige_senha ? (
                                        <Lock size={14} className="text-green-500 dark:text-green-400" />
                                    ) : (
                                        <Unlock size={14} className="text-gray-400 dark:text-gray-500" />
                                    )}
                                </div>

                                {/* Ícone de Compartilhamento (só aparece se estiver compartilhado) */}
                                {item.is_shared && (
                                    <div
                                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded transition-colors"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleShare(item);
                                        }}
                                        title="Item compartilhado - Clique para copiar link"
                                    >
                                        <Share2 size={14} className="text-blue-500 dark:text-blue-400" />
                                    </div>
                                )}

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setShowActionsMenu(showActionsMenu === item.id ? null : item.id);
                                    }}
                                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                >
                                    <MoreVertical size={16} className="text-gray-500 dark:text-gray-400" />
                                </button>

                                {/* Menu de ações */}
                                {showActionsMenu === item.id && (
                                    <div className="absolute top-8 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 z-10 min-w-[160px]">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRename(item);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                                        >
                                            <Edit3 size={16} />
                                            Renomear
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(item);
                                            }}
                                            className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                                        >
                                            <Trash2 size={16} />
                                            Deletar
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                            Modificado em {formatDate(item.updated_at)}
                        </div>

                        {!isMultiSelectMode && (
                            <div className="flex gap-2">
                                {isImageFile(item) && item.url_arquivo && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openImageLightbox(item);
                                        }}
                                        className="flex-1 px-3 py-1.5 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors flex items-center justify-center gap-1"
                                    >
                                        <ZoomIn size={12} />
                                        Visualizar
                                    </button>
                                )}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleShare(item);
                                    }}
                                    className="flex-1 px-3 py-1.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                                >
                                    <Share2 size={12} className="inline mr-1" />
                                    {item.is_shared ? 'Copiar Link' : 'Compartilhar'}
                                </button>
                                {item.type === 'file' && (
                                    <button className="px-3 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                                        <Download size={12} className="inline mr-1" />
                                        Baixar
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Upload Modal */}
            {uploadModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Upload de Arquivos
                        </h3>
                        <div className="mb-4">
                            <input
                                type="file"
                                multiple
                                onChange={(e) => setUploadFiles(e.target.files)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setUploadModalOpen(false)}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                            >
                                Cancelar
                            </button>
                            <Button
                                onClick={handleUpload}
                                variant="btn_add"
                                disabled={!uploadFiles || uploadFiles.length === 0}
                            >
                                Upload
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Folder Modal */}
            {folderModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Nova Pasta
                        </h3>
                        <div className="mb-4">
                            <input
                                type="text"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                placeholder="Nome da pasta"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setFolderModalOpen(false)}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                            >
                                Cancelar
                            </button>
                            <Button
                                onClick={handleCreateFolder}
                                variant="btn_add"
                                disabled={!newFolderName.trim()}
                            >
                                Criar Pasta
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Share Modal */}
            {shareModal.isOpen && shareModal.item && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                            Compartilhar {shareModal.item.name}
                        </h3>

                        <div className="mb-4">
                            <label className="flex items-center gap-2 mb-3">
                                <input
                                    type="checkbox"
                                    checked={shareModal.isPasswordProtected}
                                    onChange={(e) => setShareModal(prev => ({ ...prev, isPasswordProtected: e.target.checked }))}
                                    className="rounded border-gray-300 dark:border-gray-600"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Proteger com senha
                                </span>
                            </label>

                            {shareModal.isPasswordProtected && (
                                <input
                                    type="password"
                                    value={shareModal.password}
                                    onChange={(e) => setShareModal(prev => ({ ...prev, password: e.target.value }))}
                                    placeholder="Digite a senha"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                                />
                            )}
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShareModal({ item: null, isOpen: false, password: '', isPasswordProtected: false })}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                            >
                                Cancelar
                            </button>
                            <Button
                                onClick={handleShareSubmit}
                                variant="btn_add"
                                disabled={shareModal.isPasswordProtected && !shareModal.password.trim()}
                            >
                                Compartilhar
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rename Modal */}
            {renameModal.isOpen && renameModal.item && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Renomear
                            </h3>
                            <button
                                onClick={() => setRenameModal({ isOpen: false, item: null, newName: '' })}
                                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="mb-4">
                            <input
                                type="text"
                                value={renameModal.newName}
                                onChange={(e) => setRenameModal(prev => ({ ...prev, newName: e.target.value }))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleRenameSubmit();
                                    }
                                }}
                                placeholder="Digite o novo nome"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                                autoFocus
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setRenameModal({ isOpen: false, item: null, newName: '' })}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                            >
                                Cancelar
                            </button>
                            <Button
                                onClick={handleRenameSubmit}
                                variant="btn_add"
                                disabled={!renameModal.newName.trim()}
                            >
                                Renomear
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Protection Modal */}
            {protectionModal.isOpen && protectionModal.item && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                Proteger {protectionModal.item.name}
                            </h3>
                            <button
                                onClick={() => setProtectionModal({ item: null, isOpen: false, password: '' })}
                                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="mb-4">
                            <label className="text-sm text-gray-700 dark:text-gray-300 mb-2 block">
                                Digite a senha para proteger {protectionModal.item.type === 'folder' ? 'a pasta' : 'o arquivo'}
                            </label>
                            <input
                                type="password"
                                value={protectionModal.password}
                                onChange={(e) => setProtectionModal(prev => ({ ...prev, password: e.target.value }))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleProtectionSubmit();
                                    }
                                }}
                                placeholder="Digite a senha"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                                autoFocus
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setProtectionModal({ item: null, isOpen: false, password: '' })}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                            >
                                Cancelar
                            </button>
                            <Button
                                onClick={handleProtectionSubmit}
                                variant="btn_add"
                                disabled={!protectionModal.password.trim()}
                            >
                                Proteger
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Progress Modal */}
            {uploadProgress.isVisible && (
                <div className="fixed bottom-6 right-6 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl z-50">
                    {/* Header */}
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                <Upload size={20} className="text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                                    Enviando arquivos
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {uploadProgress.current} de {uploadProgress.total} arquivos
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setUploadProgress(prev => ({ ...prev, isVisible: false }))}
                            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Progress Bar */}
                    <div className="p-4">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                            ></div>
                        </div>

                        {/* File List */}
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {uploadProgress.files.map((file, index) => (
                                <div key={index} className="flex items-center gap-3 text-sm">
                                    {file.status === 'uploading' && (
                                        <div className="animate-spin">
                                            <Upload size={14} className="text-blue-600 dark:text-blue-400" />
                                        </div>
                                    )}
                                    {file.status === 'completed' && (
                                        <div className="text-green-600 dark:text-green-400">
                                            ✓
                                        </div>
                                    )}
                                    {file.status === 'error' && (
                                        <div className="text-red-600 dark:text-red-400">
                                            ✗
                                        </div>
                                    )}
                                    <span className="flex-1 truncate text-gray-700 dark:text-gray-300">
                                        {file.name}
                                    </span>
                                    {file.status === 'completed' && (
                                        <span className="text-xs text-green-600 dark:text-green-400">
                                            Concluído
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer */}
                    {uploadProgress.current === uploadProgress.total && (
                        <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/20">
                            <p className="text-sm text-green-800 dark:text-green-300 text-center font-medium">
                                ✓ Upload concluído!
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Confirm Modal */}
            {confirmModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                                    <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                    {confirmModal.title}
                                </h3>
                            </div>
                            <button
                                onClick={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => { } })}
                                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <div className="mb-6">
                            <p className="text-gray-700 dark:text-gray-300">
                                {confirmModal.message}
                            </p>
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => { } })}
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
                            >
                                Cancelar
                            </button>
                            <Button
                                onClick={() => confirmModal.onConfirm()}
                                variant="btn_add"
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                Confirmar
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox */}
            {lightbox.isOpen && lightbox.url && (
                <div
                    className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={closeLightbox}
                >
                    <div
                        className="relative max-w-4xl w-full bg-white dark:bg-gray-900 rounded-lg overflow-hidden shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate pr-4">
                                {lightbox.name}
                            </h3>
                            <div className="flex items-center gap-2">
                                <a
                                    href={lightbox.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    Abrir em nova aba
                                </a>
                                <button
                                    onClick={closeLightbox}
                                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-300"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>
                        <div className="max-h-[80vh] overflow-auto bg-gray-50 dark:bg-gray-950 p-4 flex items-center justify-center">
                            <img
                                src={lightbox.url}
                                alt={lightbox.name}
                                className="max-h-[70vh] w-auto rounded-md shadow-lg"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Container */}
            <ToastContainer toasts={toasts} />
        </div>
    );
}
